import { useEffect, useState } from 'react'
import { getUsers, createUser, updateUser, deleteUser } from '../api'
import ProcessingOverlay from '../components/ui/ProcessingOverlay'

const emptyForm = { full_name: '', username: '', password: '', role: 'BILLING_USER' }

export default function Users() {
  const [users, setUsers] = useState<any[]>([])
  const [form, setForm] = useState(emptyForm)
  const [editId, setEditId] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [processing, setProcessing] = useState('')

  const load = () => getUsers().then((r: any) => setUsers(r.data))
  useEffect(() => { load() }, []) // eslint-disable-line

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setProcessing(editId ? 'Updating user...' : 'Creating user...')
    try {
      if (editId) {
        const payload: any = { full_name: form.full_name, role: form.role }
        if (form.password) payload.password = form.password
        await updateUser(editId, payload)
      } else {
        await createUser(form)
      }
      setForm(emptyForm); setEditId(null); setShowForm(false); load()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error')
    } finally { setProcessing('') }
  }

  const startEdit = (u: any) => {
    setEditId(u.id)
    setForm({ full_name: u.full_name, username: u.username, password: '', role: u.role })
    setShowForm(true)
  }

  return (
    <div className="page">
      {processing && <ProcessingOverlay message={processing} />}
      <div className="page-header">
        <div>
          <div className="page-title">User Management</div>
          <div className="page-subtitle">Manage system users and roles</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(emptyForm); setEditId(null); setShowForm(true) }}>
          + Add User
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, marginBottom: 16, fontSize: 15 }}>{editId ? 'Edit User' : 'New User'}</div>
          {error && <div className="error-msg">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Full Name *</label>
                <input className="form-input" value={form.full_name} required onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
              </div>
              {!editId && (
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Username *</label>
                  <input className="form-input" value={form.username} required onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />
                </div>
              )}
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">{editId ? 'New Password' : 'Password *'}</label>
                <input className="form-input" type="password" value={form.password} required={!editId}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Role</label>
                <select className="form-input" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="BILLING_USER">BILLING_USER</option>
                  <option value="ADMIN">ADMIN</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" className="btn btn-primary">{editId ? 'Update' : 'Create User'}</button>
              <button type="button" className="btn btn-secondary" onClick={() => { setShowForm(false); setEditId(null); setForm(emptyForm) }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <table className="data-table">
          <thead>
            <tr><th>Name</th><th>Username</th><th>Role</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {users.map((u: any) => (
              <tr key={u.id}>
                <td style={{ fontWeight: 600 }}>{u.full_name}</td>
                <td style={{ color: '#64748b' }}>{u.username}</td>
                <td>
                  <span className={`badge ${u.role === 'ADMIN' ? 'badge-issued' : 'badge-draft'}`}>{u.role}</span>
                </td>
                <td>
                  <span className={`badge ${u.is_active ? 'badge-paid' : 'badge-cancelled'}`}>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm btn-secondary" onClick={() => startEdit(u)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={async () => { if (window.confirm('Delete user?')) { setProcessing('Deleting user...'); try { await deleteUser(u.id); load() } finally { setProcessing('') } } }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
